import { FavoriteReferenceType } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/FavoriteReferenceType';
import { setFolderFavorite } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/FavoriteService';
import {
  FavoriteEntityMessage,
  FavoriteType,
  FolderForTree,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder';
import { fetchWithParameters } from 'utils/ApiUtils';
import { registerForEventAndSubscribe } from 'utils/ClientEventUtils';
import { GetState, SetState } from 'types/zustand';
import { getFavoritesPanelTitle, getSideBarActiveTab, PortalStore, usePortalStore } from '../PortalStore';

/*
  This file is a slice of portal store, but it's own contained slice.
  Hopefully this is a way to compose more readable / testable chunks,
  and we could do code-splitting on an otherwise monolithic store this way as well.
*/

/**
 *
 * @returns slice of PortalStore state related to favorites sidebar panel.
 */
export function getFavoritesState() {
  return {
    favoriteFolders: [] as FolderForTree[],
  };
}

/**
 * @param get zustand store getter
 * @param set zustand store setter
 * @returns slice of PortalStore actions related to favorites panel
 */
export function createFavoriteActions(get: GetState<PortalStore>, set: SetState<PortalStore>) {
  return {
    initializeFavorites() {
      // initial load:
      get().actions.loadFavoriteFolders();

      // subscribe to changes:
      if (!DEV_SERVER && !IS_TEST) {
        registerForEventAndSubscribe('FavoriteEntityEventId', 'portal-store-favorites', e => {
          const data: FavoriteEntityMessage = (e as any).detail;
          if (!data || !data.Entity || data.Entity.TypeOfFavorite !== FavoriteReferenceType.Folder) {
            return;
          }

          const folderTree = usePortalStore.getState().actions;
          folderTree.refreshNode(data.Entity.ReferenceId);
          get().actions.loadFavoriteFolders();
        });
      }
    },

    async loadFavoriteFolders() {
      try {
        // using MVC controller instead of service to cut the corner or icon population for now, currently
        const response = await fetchWithParameters({ url: `/FolderData/GetPinnedFolders`, urlParams: [] });
        const favoriteFolders = await response.json();
        // const favoriteFolders = await getMyFavoritesForTree();

        set({ favoriteFolders });
      } catch (error) {
        console.error('Error loading portal favorites', error);
      }
    },

    async setFolderFavorite(folderId: string, favoriteType: FavoriteType) {
      await setFolderFavorite({ folderId, favoriteType });
    },

    async removeFolderFavorite(folderId: string) {
      const favoriteFolders = getFavoriteFolders(get()).filter(f => f.FolderID !== folderId);
      set({ favoriteFolders });
    },
  };
}

//#region selectors

export function getFavoriteFolders(state: PortalStore) {
  return state.favoriteFolders;
}

//#endregion

export function selectingFolderOnFavorites(state: PortalStore, folderId: string) {
  const isFavoritesActive = getSideBarActiveTab(state) === getFavoritesPanelTitle(state);
  return isFavoritesActive && !!getFavoriteFolders(state).find(fav => fav.FolderID === folderId);
}
